import React, { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import { ThemeProvider, useTheme } from "next-themes";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppButton from "@/components/site/WhatsAppButton";
import Home from "@/pages/Home";
import { initLenis, scrollToTop } from "@/lib/lenis";

const CoopSysPage = lazy(() => import("@/pages/CoopSysPage"));
const ServiciosPage = lazy(() => import("@/pages/ServiciosPage"));
const EmpresaPage = lazy(() => import("@/pages/EmpresaPage"));
const ClientesPage = lazy(() => import("@/pages/ClientesPage"));
const RecursosPage = lazy(() => import("@/pages/RecursosPage"));
const ArticuloPage = lazy(() => import("@/pages/ArticuloPage"));
const ContactoPage = lazy(() => import("@/pages/ContactoPage"));
const AccountPage = lazy(() => import("@/pages/AccountPage"));

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err) {
    console.error(err);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-5 p-10 text-center">
          <img
            src="/dasoft-logo.png"
            alt="DASOFT SRL"
            className="h-12 w-auto dark:rounded-lg dark:bg-white dark:px-3 dark:py-2"
          />
          <p className="font-display text-lg font-semibold">Algo salió mal.</p>
          <a href="/" className="rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-white">
            Volver al inicio
          </a>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    scrollToTop();
  }, [pathname]);
  return null;
}

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center" role="status" aria-label="Cargando">
      <img
        src="/dasoft-logo.png"
        alt="DASOFT SRL"
        className="h-12 w-auto animate-pulse dark:rounded-lg dark:bg-white dark:px-3 dark:py-2"
      />
    </div>
  );
}

function AppToaster() {
  const { theme } = useTheme();
  return <Toaster position="bottom-right" richColors closeButton theme={theme === "dark" ? "dark" : "light"} />;
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Home />} />
        <Route path="/coopsys" element={<CoopSysPage />} />
        <Route path="/servicios" element={<ServiciosPage />} />
        <Route path="/empresa" element={<EmpresaPage />} />
        <Route path="/clientes" element={<ClientesPage />} />
        <Route path="/recursos" element={<RecursosPage />} />
        <Route path="/recursos/:slug" element={<ArticuloPage />} />
        <Route path="/contacto" element={<ContactoPage />} />
        <Route path="/account/:token" element={<AccountPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

function AppShell() {
  const { pathname } = useLocation();
  const isPortal = pathname.startsWith("/account");
  return (
    <>
      {!isPortal && <Navbar />}
      <ScrollManager />
      <Suspense fallback={<PageLoader />}>
        <AnimatedRoutes />
        {!isPortal && <Footer />}
        {!isPortal && <WhatsAppButton />}
      </Suspense>
      <AppToaster />
    </>
  );
}

export default function App() {
  useEffect(() => {
    initLenis();
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} themes={["light", "dark"]}>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
